import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ImageIcon,
  MessageSquare,
  Megaphone,
  Paperclip,
  Plus,
  Send,
  Smartphone,
  Users,
  X,
} from 'lucide-react';

import {
  commsApi,
  type Broadcast,
  type BroadcastAudience,
  type Conversation,
  type Message,
  type ParticipantKind,
  type PushToken,
} from '@/features/comms/api';
import { fieldUserApi, tenantApi } from '@/features/auth/api';
import { operationsApi } from '@/features/operations/api';
import { useAuthStore } from '@/stores/auth-store';
import { PageHeader, StatusPill } from '@/lib/ui';

type Tab = 'dms' | 'groups' | 'broadcasts' | 'push';

type AttachmentDraft = {
  file: File;
  previewUrl: string;
};

/** Resolve a broadcast's audience to an explicit recipient list, since the
 *  backend send endpoint expects concrete (kind, id) pairs. */
async function resolveRecipients(
  audience: BroadcastAudience,
  ref: Record<string, unknown>,
): Promise<[ParticipantKind, string][]> {
  if (audience === 'FIELD_USERS') {
    const users = await fieldUserApi.list();
    return users.map((u) => ['FIELD_USER', u.id]);
  }
  if (audience === 'WEB_USERS') {
    const members = await tenantApi.members();
    return members.map((m) => ['USER', m.user_id]);
  }
  if (audience === 'ALL') {
    const [users, members] = await Promise.all([fieldUserApi.list(), tenantApi.members()]);
    return [
      ...users.map((u) => ['FIELD_USER', u.id] as [ParticipantKind, string]),
      ...members.map((m) => ['USER', m.user_id] as [ParticipantKind, string]),
    ];
  }
  if (audience === 'ENGAGEMENT') {
    const id = ref.id as string | undefined;
    if (!id) return [];
    const assignments = await operationsApi.listAssignments(id);
    return assignments.filter((a) => a.is_active).map((a) => ['FIELD_USER', a.field_user_id]);
  }
  if (audience === 'ROLE') {
    const role = ref.role as string | undefined;
    const members = await tenantApi.members();
    return members.filter((m) => !role || m.role === role).map((m) => ['USER', m.user_id]);
  }
  return [];
}

export function CommsPage() {
  const [tab, setTab] = useState<Tab>('dms');
  const [activeId, setActiveId] = useState<string | null>(null);
  const can = (perm: string) => useAuthStore.getState().tenant?.permissions.includes(perm);
  const queryClient = useQueryClient();

  const conversations = useQuery({
    queryKey: ['comms', 'conversations'],
    queryFn: () => commsApi.listConversations(),
    refetchInterval: 15_000,
  });

  const broadcasts = useQuery({
    queryKey: ['comms', 'broadcasts'],
    queryFn: () => commsApi.listBroadcasts(),
    refetchInterval: 30_000,
  });

  const filteredConversations = useMemo(() => {
    const all = conversations.data ?? [];
    if (tab === 'dms') return all.filter((c) => c.channel === 'DIRECT');
    if (tab === 'groups') return all.filter((c) => c.channel === 'GROUP');
    return [];
  }, [conversations.data, tab]);

  useEffect(() => {
    setActiveId(null);
  }, [tab]);

  const messages = useQuery({
    queryKey: ['comms', 'messages', activeId],
    queryFn: () => commsApi.listMessages(activeId!, 200),
    enabled: !!activeId && (tab === 'dms' || tab === 'groups'),
    refetchInterval: 8_000,
  });

  const send = useMutation({
    mutationFn: ({
      id,
      body,
      attachment,
    }: {
      id: string;
      body: string;
      attachment?: AttachmentDraft;
    }) =>
      commsApi.sendMessage(
        id,
        body,
        attachment
          ? {
              kind: 'ATTACHMENT',
              attachment_path: attachment.file.name,
              attachment_mime: attachment.file.type,
            }
          : undefined,
      ),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['comms', 'messages', vars.id] });
      queryClient.invalidateQueries({ queryKey: ['comms', 'conversations'] });
    },
  });

  const remove = useMutation({
    mutationFn: (messageId: string) => commsApi.deleteMessage(messageId),
    onSuccess: () => {
      if (activeId) queryClient.invalidateQueries({ queryKey: ['comms', 'messages', activeId] });
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => commsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comms', 'conversations'] }),
  });

  const [showCompose, setShowCompose] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const isThreadTab = tab === 'dms' || tab === 'groups';

  const tabMeta: Record<Tab, { label: string; icon: typeof MessageSquare }> = {
    dms: { label: 'Direct messages', icon: MessageSquare },
    groups: { label: 'Project groups', icon: Users },
    broadcasts: { label: 'Broadcasts', icon: Megaphone },
    push: { label: 'Push tokens', icon: Smartphone },
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col gap-4 p-6">
      <PageHeader
        title="Communication"
        icon={MessageSquare}
        subtitle="Direct messages, project groups, broadcasts, and push devices."
        actions={
          <>
            {can('conversation.send') && (
              <button className="btn-secondary" onClick={() => setShowCompose(true)}>
                <Plus className="h-4 w-4" />
                New DM
              </button>
            )}
            {can('broadcast.send') && (
              <button className="btn-primary" onClick={() => setShowBroadcast(true)}>
                <Megaphone className="h-4 w-4" />
                New broadcast
              </button>
            )}
          </>
        }
      />

      <nav className="flex gap-1 border-b border-border text-sm">
        {(['dms', 'groups', 'broadcasts', 'push'] as Tab[]).map((t) => {
          const Icon = tabMeta[t].icon;
          const active = tab === t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 transition-colors ${
                active
                  ? 'border-primary font-medium text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tabMeta[t].label}
            </button>
          );
        })}
      </nav>

      {tab === 'broadcasts' ? (
        <BroadcastPanel rows={broadcasts.data ?? []} loading={broadcasts.isLoading} />
      ) : tab === 'push' ? (
        <PushTokensPanel />
      ) : (
        <div className="grid flex-1 grid-cols-[280px_1fr] gap-4 overflow-hidden">
          <aside className="card overflow-auto !p-0">
            <header className="border-b border-border px-3 py-2 text-xs uppercase text-muted-foreground">
              {filteredConversations.length} thread{filteredConversations.length === 1 ? '' : 's'}
            </header>
            {conversations.isLoading ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">Loading…</p>
            ) : filteredConversations.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">No conversations yet.</p>
            ) : (
              <ul>
                {filteredConversations.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => {
                        setActiveId(c.id);
                        markRead.mutate(c.id);
                      }}
                      className={`block w-full border-b border-border px-3 py-2 text-left text-sm ${
                        activeId === c.id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="font-medium">
                        {c.title ?? (c.channel === 'DIRECT' ? 'Direct message' : 'Conversation')}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {c.last_message_preview ?? 'No messages yet'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
          <section className="card flex flex-col overflow-hidden !p-0">
            {activeId && isThreadTab ? (
              <ConversationThread
                conversation={filteredConversations.find((c) => c.id === activeId) ?? null}
                messages={messages.data ?? []}
                loading={messages.isLoading}
                onSend={(body, attachment) => send.mutate({ id: activeId, body, attachment })}
                onDelete={(messageId) => remove.mutate(messageId)}
                sending={send.isPending}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Select a conversation to read messages.
              </div>
            )}
          </section>
        </div>
      )}

      {showCompose && (
        <NewDirectModal
          onClose={() => setShowCompose(false)}
          onCreated={(c) => {
            setShowCompose(false);
            setTab('dms');
            setActiveId(c.id);
            queryClient.invalidateQueries({ queryKey: ['comms', 'conversations'] });
          }}
        />
      )}
      {showBroadcast && (
        <BroadcastComposerModal
          onClose={() => setShowBroadcast(false)}
          onCreated={() => {
            setShowBroadcast(false);
            queryClient.invalidateQueries({ queryKey: ['comms', 'broadcasts'] });
          }}
        />
      )}
    </div>
  );
}

function ConversationThread({
  conversation,
  messages,
  loading,
  onSend,
  onDelete,
  sending,
}: {
  conversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  onSend: (body: string, attachment?: AttachmentDraft) => void;
  onDelete: (messageId: string) => void;
  sending: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<AttachmentDraft | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const me = useAuthStore.getState().user;

  // Basic local typing indicator. A real WebSocket presence feed would
  // replace this once the realtime channel is live.
  const onDraftChange = (value: string) => {
    setDraft(value);
    setIsTyping(value.trim().length > 0);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setIsTyping(false), 2500);
  };

  useEffect(() => {
    return () => {
      if (attachment) URL.revokeObjectURL(attachment.previewUrl);
    };
  }, [attachment]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body && !attachment) return;
    onSend(body || `📎 ${attachment?.file.name}`, attachment ?? undefined);
    setDraft('');
    setAttachment(null);
    setIsTyping(false);
  };

  return (
    <>
      <header className="border-b border-border px-4 py-2">
        <div className="font-medium">
          {conversation?.title ??
            (conversation?.channel === 'DIRECT' ? 'Direct message' : 'Conversation')}
        </div>
        <div className="text-xs text-muted-foreground">
          {conversation?.last_message_at
            ? `Last message ${new Date(conversation.last_message_at).toLocaleString()}`
            : 'No messages yet'}
        </div>
      </header>
      <div className="flex-1 space-y-2 overflow-auto p-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Start the conversation.</p>
        ) : (
          messages.map((m) => {
            const mine = !!me && m.sender_kind === 'USER' && m.sender_id === me.userId;
            return (
              <div
                key={m.id}
                className={`group relative max-w-[75%] rounded px-3 py-2 text-sm ${
                  mine ? 'ml-auto bg-primary text-primary-foreground' : 'bg-muted'
                }`}
              >
                {m.deleted_at ? (
                  <em className="text-xs">Message deleted</em>
                ) : (
                  <>
                    {m.kind === 'ATTACHMENT' && m.attachment_path ? (
                      <div className="mb-1 flex items-center gap-1 text-xs">
                        <span>{m.attachment_mime?.startsWith('image/') ? '🖼' : '📎'}</span>
                        <span className="truncate">{m.attachment_path}</span>
                      </div>
                    ) : null}
                    {m.body ? <div>{m.body}</div> : null}
                    <div className="mt-1 text-[10px] opacity-70">
                      {new Date(m.created_at).toLocaleTimeString()}
                    </div>
                    {mine && (
                      <button
                        type="button"
                        onClick={() => onDelete(m.id)}
                        className="absolute -top-2 right-1 hidden rounded bg-destructive px-1.5 py-0.5 text-[10px] text-destructive-foreground group-hover:block"
                        aria-label="Delete message"
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
        {isTyping && (
          <div className="flex items-center gap-1 px-1 text-xs text-muted-foreground">
            <span className="inline-flex gap-0.5">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
            </span>
            typing…
          </div>
        )}
      </div>
      {attachment && (
        <div className="flex items-center gap-2 border-t border-border px-3 py-2 text-xs">
          {attachment.file.type.startsWith('image/') ? (
            <img
              src={attachment.previewUrl}
              alt={attachment.file.name}
              className="h-10 w-10 rounded-md object-cover ring-1 ring-border"
            />
          ) : (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="truncate">{attachment.file.name}</span>
          <button
            type="button"
            className="btn-ghost btn-sm ml-auto"
            onClick={() => setAttachment(null)}
            aria-label="Remove attachment"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <form className="flex items-center gap-2 border-t border-border p-3" onSubmit={submit}>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setAttachment({ file, previewUrl: URL.createObjectURL(file) });
            }
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => fileInput.current?.click()}
          aria-label="Attach image"
          title="Attach image"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="Type a message…"
          className="input flex-1"
        />
        <button
          type="submit"
          className="btn-primary"
          disabled={sending || (!draft.trim() && !attachment)}
        >
          <Send className="h-4 w-4" />
          Send
        </button>
      </form>
    </>
  );
}

function BroadcastPanel({ rows, loading }: { rows: Broadcast[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const sendOne = useMutation({
    mutationFn: async (b: Broadcast) => {
      const recipients = await resolveRecipients(b.audience, b.audience_ref);
      if (recipients.length === 0) {
        throw new Error('No recipients resolved for this audience.');
      }
      return commsApi.sendBroadcast(b.id, recipients);
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comms', 'broadcasts'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const cancelOne = useMutation({
    mutationFn: (b: Broadcast) => commsApi.cancelBroadcast(b.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comms', 'broadcasts'] }),
  });

  return (
    <section className="data-table-wrapper flex-1 overflow-auto">
      {error && (
        <div className="border-b border-border bg-danger/10 px-4 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Audience</th>
              <th>Status</th>
              <th className="num">Recipients</th>
              <th className="num">Read</th>
              <th>Sent</th>
              <th className="num"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="muted px-4 py-6" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="muted px-4 py-6" colSpan={7}>
                  No broadcasts yet.
                </td>
              </tr>
            ) : (
              rows.map((b) => {
                const canSend = b.status === 'DRAFT' || b.status === 'SCHEDULED';
                const busy =
                  (sendOne.isPending && sendOne.variables?.id === b.id) ||
                  (cancelOne.isPending && cancelOne.variables?.id === b.id);
                return (
                  <tr key={b.id}>
                    <td className="font-medium">{b.title}</td>
                    <td className="muted">{b.audience}</td>
                    <td>
                      <StatusPill status={b.status} />
                    </td>
                    <td className="num">{b.recipient_count}</td>
                    <td className="num">
                      {b.recipient_count > 0
                        ? `${Math.round((b.read_count / b.recipient_count) * 100)}%`
                        : '—'}
                    </td>
                    <td className="muted">
                      {b.sent_at ? new Date(b.sent_at).toLocaleString() : '—'}
                    </td>
                    <td className="actions">
                      {canSend && (
                        <div className="flex justify-end gap-1.5">
                          <button
                            className="btn-primary btn-sm"
                            disabled={busy}
                            onClick={() => sendOne.mutate(b)}
                          >
                            <Send className="h-3.5 w-3.5" />
                            Send
                          </button>
                          <button
                            className="btn-secondary btn-sm"
                            disabled={busy}
                            onClick={() => cancelOne.mutate(b)}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PushTokensPanel() {
  const tokens = useQuery({
    queryKey: ['comms', 'push-tokens'],
    queryFn: () => commsApi.listPushTokens(),
  });
  const rows: PushToken[] = tokens.data ?? [];
  return (
    <section className="data-table-wrapper flex-1 overflow-auto">
      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Platform</th>
              <th>Device</th>
              <th>Last seen</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tokens.isLoading ? (
              <tr>
                <td className="muted px-4 py-6" colSpan={5}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="muted px-4 py-6" colSpan={5}>
                  No registered push tokens.
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="font-mono text-xs">{t.principal_id}</span>
                    <span className="pill-muted ml-2">{t.principal_kind}</span>
                  </td>
                  <td className="uppercase">{t.platform}</td>
                  <td className="muted">{t.device_label ?? '—'}</td>
                  <td className="muted">
                    {t.last_seen_at ? new Date(t.last_seen_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <StatusPill status={t.revoked_at ? 'REVOKED' : 'ACTIVE'} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type RecipientOption = {
  kind: ParticipantKind;
  id: string;
  label: string;
};

function NewDirectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: Conversation) => void;
}) {
  const [selected, setSelected] = useState('');
  const [filter, setFilter] = useState('');

  const options = useQuery({
    queryKey: ['comms', 'dm-recipients'],
    queryFn: async (): Promise<RecipientOption[]> => {
      const [fieldUsers, members] = await Promise.all([
        fieldUserApi.list(),
        tenantApi.members().catch(() => []),
      ]);
      return [
        ...fieldUsers.map((u) => ({
          kind: 'FIELD_USER' as ParticipantKind,
          id: u.id,
          label: `${u.employee_id} · ${u.full_name} (field)`,
        })),
        ...members.map((m) => ({
          kind: 'USER' as ParticipantKind,
          id: m.user_id,
          label: `${m.full_name} · ${m.email} (web)`,
        })),
      ];
    },
  });

  const filtered = useMemo(() => {
    const all = options.data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) => o.label.toLowerCase().includes(q));
  }, [options.data, filter]);

  const chosen = (options.data ?? []).find((o) => `${o.kind}:${o.id}` === selected);

  const create = useMutation({
    mutationFn: () => {
      if (!chosen) throw new Error('Select a recipient');
      return commsApi.createDirect(chosen.kind, chosen.id);
    },
    onSuccess: onCreated,
  });

  return (
    <Modal title="Start a direct message" onClose={onClose}>
      <label className="block text-sm">
        Search recipient
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Name, employee ID, or email…"
          className="input mt-1 w-full"
        />
      </label>
      <label className="mt-3 block text-sm">
        Recipient
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="input mt-1 w-full"
          size={6}
          disabled={options.isLoading}
        >
          {options.isLoading ? (
            <option value="">Loading…</option>
          ) : filtered.length === 0 ? (
            <option value="">No matching users</option>
          ) : (
            filtered.map((o) => (
              <option key={`${o.kind}:${o.id}`} value={`${o.kind}:${o.id}`}>
                {o.label}
              </option>
            ))
          )}
        </select>
      </label>
      {create.isError && (
        <p className="mt-2 text-sm text-destructive">{(create.error as Error).message}</p>
      )}
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!chosen || create.isPending}
          onClick={() => create.mutate()}
        >
          Start
        </button>
      </footer>
    </Modal>
  );
}

function BroadcastComposerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<BroadcastAudience>('FIELD_USERS');
  const [requiresAck, setRequiresAck] = useState(false);
  const [roleRef, setRoleRef] = useState('');
  const [engagementId, setEngagementId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const engagements = useQuery({
    queryKey: ['comms', 'engagements'],
    queryFn: () => operationsApi.listEngagements(),
    enabled: audience === 'ENGAGEMENT',
  });

  const buildRef = (): Record<string, unknown> => {
    if (audience === 'ROLE') return roleRef ? { role: roleRef } : {};
    if (audience === 'ENGAGEMENT') return engagementId ? { id: engagementId } : {};
    return {};
  };

  const saveDraft = useMutation({
    mutationFn: () =>
      commsApi.createBroadcast({
        title,
        body,
        audience,
        audience_ref: buildRef(),
        requires_acknowledgement: requiresAck,
      }),
    onSuccess: onCreated,
    onError: (e) => setError((e as Error).message),
  });

  const createAndSend = useMutation({
    mutationFn: async () => {
      const ref = buildRef();
      const recipients = await resolveRecipients(audience, ref);
      if (recipients.length === 0) {
        throw new Error('No recipients resolved for this audience.');
      }
      const bc = await commsApi.createBroadcast({
        title,
        body,
        audience,
        audience_ref: ref,
        requires_acknowledgement: requiresAck,
      });
      return commsApi.sendBroadcast(bc.id, recipients);
    },
    onSuccess: onCreated,
    onError: (e) => setError((e as Error).message),
  });

  const busy = saveDraft.isPending || createAndSend.isPending;
  const valid =
    title.trim() &&
    body.trim() &&
    (audience !== 'ENGAGEMENT' || engagementId) &&
    (audience !== 'ROLE' || roleRef.trim());

  return (
    <Modal title="New broadcast" onClose={onClose}>
      <label className="block text-sm">
        Title
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input mt-1 w-full"
        />
      </label>
      <label className="mt-3 block text-sm">
        Message
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          className="input mt-1 w-full"
        />
      </label>
      <label className="mt-3 block text-sm">
        Audience
        <select
          value={audience}
          onChange={(e) => setAudience(e.target.value as BroadcastAudience)}
          className="input mt-1 w-full"
        >
          <option value="FIELD_USERS">All field users</option>
          <option value="ALL">All members</option>
          <option value="WEB_USERS">All web users</option>
          <option value="ENGAGEMENT">Specific engagement…</option>
          <option value="ROLE">By role…</option>
        </select>
      </label>
      {audience === 'ENGAGEMENT' && (
        <label className="mt-3 block text-sm">
          Engagement
          <select
            value={engagementId}
            onChange={(e) => setEngagementId(e.target.value)}
            className="input mt-1 w-full"
            disabled={engagements.isLoading}
          >
            <option value="">{engagements.isLoading ? 'Loading…' : 'Select an engagement…'}</option>
            {(engagements.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} · {e.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {audience === 'ROLE' && (
        <label className="mt-3 block text-sm">
          Role slug
          <input
            value={roleRef}
            onChange={(e) => setRoleRef(e.target.value)}
            className="input mt-1 w-full"
          />
        </label>
      )}
      <label className="mt-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={requiresAck}
          onChange={(e) => setRequiresAck(e.target.checked)}
        />
        Require acknowledgement
      </label>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-secondary"
          disabled={!valid || busy}
          onClick={() => {
            setError(null);
            saveDraft.mutate();
          }}
        >
          Save draft
        </button>
        <button
          className="btn-primary"
          disabled={!valid || busy}
          onClick={() => {
            setError(null);
            createAndSend.mutate();
          }}
        >
          Send now
        </button>
      </footer>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div className="card w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
